import React, { useEffect, useMemo, useState } from "react";
import {
  TableCellsMerge,
  FileSliders,
  Sheet,
  ArrowUp,
  ArrowDown,
  SquarePen,
} from "lucide-react";
import { FaArrowLeft } from "react-icons/fa";
import { CiCircleInfo } from "react-icons/ci";
import heartLoading from "../../../assests/heartLoading.gif";
import axiosInstance from "../../../axiosInstanceSetup";

import { useSearchParams } from "react-router-dom";
import ERLandingPage from "../ERDiagram/pages/ERLandingPage";
import BoxComponent from "./BoxComponent";
import { ReportUsagetrend } from "./ReportUsagetrend";
import ReportLIneage from "./ReportLIneage";
import { useFeatureFlag } from "../../../context/FeatureFlagContext";


const serviceNowTicketLink = "https://marsprod.service-now.com/sp?id=incident_helper";

const Tooltip = ({ children, content }) => {
  const [isVisible, setIsVisible] = useState(false);
  if (!content || content.length <= 50) return children;
  return (
    <div className="relative inline-block">
      <div onMouseEnter={() => setIsVisible(true)} onMouseLeave={() => setIsVisible(false)}>
        {children}
      </div>
      {isVisible && (
        <div className="absolute bottom-full left-0 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded shadow-lg z-50 max-w-sm break-words whitespace-normal">
          {content}
        </div>
      )}
    </div>
  );
};

const TruncatedCell = ({ text, maxLength = 50 }) => {
  if (!text) return "-";
  const displayText = text.length > maxLength ? `${text.slice(0, maxLength)}...` : text;
  return <Tooltip content={text}><span>{displayText}</span></Tooltip>;
};

const getLayerClasses = (layer) => {
  switch (layer?.toLowerCase()) {
    case "bronze": return "bg-amber-700 text-white";
    case "silver": return "bg-gray-300 text-gray-900";
    case "gold": return "bg-yellow-400 text-gray-900";
    case "ingestion": return "bg-emerald-500 text-white";
    case "analyticalproduct": return "bg-indigo-600 text-white";
    default: return "bg-gray-500 text-white";
  }
};

 export const ReportKPITab = ({ data, loading }) => {
  const [searchValues, setSearchValues] = useState({});
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
    const isAIEditEnabledReport= useFeatureFlag("aiMetadataReport");


  const columns = ["kpi_name", "definition", "measure", "expression", "action"];
  const columnLabels = {
    kpi_name: "KPI Name",
    expression: "Expression",
    definition: "Definition",
    measure: "Measure",
    action: "Action",
  };

  const filteredData = useMemo(() => {
    if (!data) return [];
    return data.filter((row) =>
      columns.every((col) => {
        if (col === "action") return true;
        const searchValue = searchValues[col] || "";
        if (!searchValue) return true;
        return String(row[col] || "").toLowerCase().includes(searchValue.toLowerCase());
      })
    );
  }, [data, searchValues]);

  const sortedData = useMemo(() => {
    if (!sortConfig?.key || sortConfig.key === "action") return filteredData;
    return [...filteredData].sort((a, b) => {
      const aValue = a[sortConfig.key];
      const bValue = b[sortConfig.key];
      if (aValue == null) return 1;
      if (bValue == null) return -1;
      if (typeof aValue === "number") {
        return sortConfig.direction === "asc" ? aValue - bValue : bValue - aValue;
      }
      const aStr = String(aValue).toLowerCase();
      const bStr = String(bValue).toLowerCase();
      return sortConfig.direction === "asc" ? aStr.localeCompare(bStr) : bStr.localeCompare(aStr);
    });
  }, [filteredData, sortConfig]);

  const handleSort = (key) => {
    if (key === "action") return;
    setSortConfig((prev) =>
      prev.key === key
        ? { key, direction: prev.direction === "asc" ? "desc" : "asc" }
        : { key, direction: "asc" }
    );
  };

  const handleSearch = (key, value) => {
    setSearchValues((prev) => ({ ...prev, [key]: value }));
  };

  if (loading) {
    return (
       <div className="fixed inset-0 bg-white flex items-center justify-center z-50 w-full h-full">
        <div className="flex flex-col items-center space-y-3">
          <img src={heartLoading} alt="Loading..." style={{ width : 200}} />
          <span className="text-gray-700 text-[20px]">
            Please wait while loading the resources...
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto border border-gray-200 rounded-lg">
      <table className="w-full border-collapse text-sm">
        <thead>
          <tr className="bg-teal-50">
            {columns.map((col) => (
              <th key={col} className="px-3.5 py-2.5 text-left font-medium text-xs text-gray-700 border-b border-gray-200">
                <div className="flex items-center gap-1 mb-2">
                  <span className="font-semibold">{columnLabels[col]}</span>
                  {col !== "action" && (
                    <button
                      onClick={() => handleSort(col)}
                      className="p-1 rounded hover:bg-gray-200 transition-colors"
                    >
                      {!sortConfig || sortConfig.key !== col ? (
                        <ArrowUp size={14} className="text-gray-400" />
                      ) : sortConfig.direction === "asc" ? (
                        <ArrowUp size={14} className="text-teal-600" />
                      ) : (
                        <ArrowDown size={14} className="text-teal-600" />
                      )}
                    </button>
                  )}
                </div>
                {col !== "action" && (
                  <input
                    type="text"
                    value={searchValues[col] || ""}
                    onChange={(e) => handleSearch(col, e.target.value)}
                    placeholder="Search"
                    className="w-full px-2.5 py-1.5 text-xs rounded text-gray-500 placeholder-gray-400"
                  />
                )}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sortedData && sortedData.length > 0 ? (
            sortedData.map((row, i) => (
              <tr key={row.kpi_id} className={`border-b border-gray-100 ${i % 2 === 0 ? "bg-white" : "bg-gray-50"}`}>
                <td className="px-3.5 py-2.5">
                  <TruncatedCell text={row.kpi_name} maxLength={60} />
                </td>
                <td className="px-3.5 py-2.5 break-words">
                  <TruncatedCell text={row.definition} maxLength={60} />
                </td>
                <td className="px-3.5 py-2.5 w-[100px]">
                  {row.measure ? (
                    <span className="inline-block bg-blue-50 text-blue-900 px-2 py-0.5 rounded-full text-xs font-medium">
                      {row.measure}
                    </span>
                  ) : "N/A"}
                </td>
                <td className="px-3.5 py-2.5 max-w-xs">
                  <pre className="text-xs p-2 rounded bg-gray-50 overflow-y-auto whitespace-pre-wrap break-words m-0 font-mono max-h-24">
                    {row.expression || "N/A"}
                  </pre>
                </td>
                {isAIEditEnabledReport && (
                  <td className="px-3.5 py-2.5 w-[100px]">
                    <button
                      disabled
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-[#99f6e4] text-[#0d9488] bg-white text-[11px] font-medium whitespace-nowrap disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" style={{ display: "inline", verticalAlign: "middle" }}>
                        <path d="M12 2L14.09 8.26L21 9.27L16 14.14L17.18 21.02L12 17.77L6.82 21.02L8 14.14L3 9.27L9.91 8.26L12 2Z" />
                      </svg>
                      AI Edit
                    </button>
                  </td>
                )}
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan={4} className="px-3.5 py-5 text-center text-gray-400 text-sm">
                No data available
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

const PlaceholderTab = ({ title }) => (
  <div className="bg-gray-50 rounded-lg min-h-56 flex items-center justify-center text-gray-400 text-sm">
    {title} coming soon
  </div>
);

export const AssetsDetailsUI = ({ onBack, reportData, reportLoading }) => {
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState("Report KPI");

  // Usage state
  const [usageData, setUsageData] = useState(null);
  const [usageLoading, setUsageLoading] = useState(false);
  const [usagePage, setUsagePage] = useState(1);
  const [accumulatedUsers, setAccumulatedUsers] = useState([]);
  const [businessHasMore, setBusinessHasMore] = useState(false);
  const [isFetchingMoreBusiness, setIsFetchingMoreBusiness] = useState(false);

  const [reportSortBy, setReportSortBy] = useState("");
  const [reportSortOrder, setReportSortOrder] = useState("desc");
  const [reportColumnSearch, setReportColumnSearch] = useState({});

  const isKpi = searchParams.get("type") === "kpi";
  const l1 = searchParams.get("l1");
  const l2 = searchParams.get("l2");
  const l3 = searchParams.get("l3");
  const l4 = searchParams.get("l4");
  const l5 = searchParams.get("l5");
  const reportSourceType = searchParams.get("source_type") || "uc";

  const fetchUsageWithParams = async (page, sortBy, sortOrder, columnSearch, append = false) => {
    if (!l5) return;
    if (append) {
      setIsFetchingMoreBusiness(true);
    } else {
      setUsageLoading(true);
    }
    try {
      const params = new URLSearchParams();
      params.append("page", String(page));
      params.append("page_size", "10");
      params.append("sort_order", sortOrder || "desc");
      if (sortBy) params.append("sort_by", sortBy);
      const activeSearchKey = Object.keys(columnSearch || {})[0];
      if (activeSearchKey && columnSearch[activeSearchKey]) {
        params.append("search_by", activeSearchKey);
        params.append("search_value", columnSearch[activeSearchKey]);
      }
      const response = await axiosInstance.get(
        `${process.env.REACT_APP_API_URL}/report_data_insights/${encodeURIComponent(l5)}?${params.toString()}`
      );
      if (response.status === 200) {
        if (append) {
          setAccumulatedUsers((prev) => [...prev, ...(response.data.business_users || [])]);
        } else {
          setUsageData(response.data);
          setAccumulatedUsers(response.data.business_users || []);
          setUsagePage(page);
        }
        const pagination = response.data.pagination;
        setBusinessHasMore(page < (pagination?.total_pages ?? 1));
      }
    } catch (e) {
      console.error("Error fetching usage data:", e);
    } finally {
      setIsFetchingMoreBusiness(false);
      setUsageLoading(false);
    }
  };

  // Fetch on tab switch
  useEffect(() => {
    if (activeTab !== "Report Usage") return;
    if (!l5) return;
    setAccumulatedUsers([]);
    setUsagePage(1);
    fetchUsageWithParams(1, reportSortBy, reportSortOrder, reportColumnSearch);
  }, [activeTab, l5]);

  const handleReportSortChange = (key, order) => {
    setReportSortBy(key);
    setReportSortOrder(order);
    setAccumulatedUsers([]);
    setUsagePage(1);
    fetchUsageWithParams(1, key, order, reportColumnSearch);
  };

  const handleReportSearchChange = (key, value) => {
    const newSearch = value ? { [key]: value } : {};
    setReportColumnSearch(newSearch);
    setAccumulatedUsers([]);
    setUsagePage(1);
    fetchUsageWithParams(1, reportSortBy, reportSortOrder, newSearch);
  };

  // Load more
  const loadMoreBusiness = async () => {
    if (!l5 || isFetchingMoreBusiness) return;
    const nextPage = usagePage + 1;
    setUsagePage(nextPage);
    await fetchUsageWithParams(nextPage, reportSortBy, reportSortOrder, reportColumnSearch, true);
  };

  const effectiveReportData = reportData;
  const effectiveLoading = Boolean(reportLoading);

  const kpiData = useMemo(() => {
    return effectiveReportData?.data || effectiveReportData || [];
  }, [effectiveReportData]);

  const assetData =
    effectiveReportData && effectiveReportData.length > 0
      ? effectiveReportData[0]
      : null;

  const tabs = [
    { id: "Report KPI", label: "Report KPI", icon: TableCellsMerge },
    { id: "Report Usage", label: "Report Usage", icon: Sheet },
    { id: "Report Lineage", label: "Report Lineage", icon: FileSliders },
  ];

  return (
    <div className="space-y-6 p-4">
      <div className="rounded-lg pt-4 relative">
        <div className="flex justify-between items-center flex-wrap gap-4 mb-1">
          <h1 className="text-2xl font-bold text-black break-words whitespace-normal overflow-hidden max-w-4xl">
            {assetData?.report_name || "N/A"}
          </h1>
          <button
            onClick={onBack}
            className="h-10 px-4 py-2 rounded-lg flex items-center gap-2 transition-colors duration-200 border border-blue-900 text-blue-900 hover:bg-blue-900 hover:text-white hover:shadow-lg whitespace-nowrap"
          >
            <FaArrowLeft /> Go Back
          </button>
        </div>

        <p className="text-lg text-black mb-4 leading-relaxed">
          {assetData?.definition || "N/A"}
        </p>

        <div className="flex justify-start mb-3 gap-2 items-center flex-wrap">
          <ERLandingPage />
             <a
            href={serviceNowTicketLink}
            target="_blank"
            rel="noreferrer"
            className="h-10 px-4 py-2 rounded-lg border border-pink-100 flex items-center gap-2 text-red-700 whitespace-nowrap hover:bg-red-50 transition-colors"
          >
            <CiCircleInfo size={20} /> Raise an Incident
          </a>
        </div>

        
      </div>

      <div className="border-b border-gray-200">
        <nav className="flex space-x-8 overflow-x-auto pb-2">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`whitespace-nowrap flex-shrink-0 flex items-center gap-2 py-3 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === tab.id
                  ? "border-[#bb1ccc] text-[#bb1ccc]"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
              }`}
            >
              <tab.icon size={18} />
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      <div className="space-y-6">
        {activeTab === "Report KPI" && (
          <ReportKPITab data={kpiData} loading={effectiveLoading} />
        )}

        {activeTab === "Report Usage" && (
          <ReportUsagetrend
            usageData={usageData}
            loading={usageLoading}
            accumulatedUsers={accumulatedUsers}
            loadMoreBusiness={loadMoreBusiness}
            businessHasMore={businessHasMore}
            isFetchingMoreBusiness={isFetchingMoreBusiness}
            sortBy={reportSortBy}                      
            sortOrder={reportSortOrder}                 
            columnSearch={reportColumnSearch}           
            onSortChange={handleReportSortChange}       
            onSearchChange={handleReportSearchChange} 
            hasInteracted={false}
          />
        )}

        {activeTab === "Report Lineage" && (
          <ReportLIneage reportName={l5 || ""} sourceType={reportSourceType} />
        )}
      </div>
    </div>
  );
};

export default AssetsDetailsUI;